<template>
  <l-chip
    label
    class="z-[1] cursor-auto"
    :color="props.color || 'initial'"
    :text-color="props.textColor"
    :no-opacity="props.noOpacity"
    :close="false"
  >
    <fp-tooltip max-width="365px" class="z-[10000]">
      <div>
        <v-icon class="-mt-1" small>{{ isTimerActive ? iconTimerActive : iconTimerInactive }}</v-icon>
        <span v-if="timeRemaining" class="font-weight-medium mx-1">
        {{timeRemaining}}
        </span>
      </div>
      <template v-slot:tooltipContent>
        <span class="headline-6">{{tooltipTitle}}</span>
        <br>
        <span class="body-3">{{tooltipText}}</span>
      </template>
    </fp-tooltip>
  </l-chip>
</template>

<script setup lang="ts">

import LChip from '@/components/commons/LChip.vue';
import moment from 'moment/moment';
import {computed, onMounted, PropType, ref} from 'vue';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import DateUtils from '@/services/utils/DateUtils';
import FpTooltip from '@/components/fpComponents/FpTooltip.vue';
import {Moment} from 'moment';

const props = defineProps({
  // `Moment` to interfejs — w deklaracji type-only kompilator `<script setup>` nie ma z czego
  // zbudować runtime'owego konstruktora i wstawia `null`, co Vue raportuje jako
  // `Invalid prop type: "null" is not a constructor`. Dlatego runtime typy podajemy wprost.
  endDate: {
    type: [String, Date, Object,] as PropType<string | Moment | Date>,
    required: true,
  },
  iconTimerActive: {
    type: String as PropType<string>,
    required: true,
  },
  iconTimerInactive: {
    type: String as PropType<string>,
    required: true,
  },
  tooltipTitle: String as PropType<string>,
  tooltipText: String as PropType<string>,
  color: String as PropType<string>,
  textColor: String as PropType<string>,
  noOpacity: Boolean as PropType<boolean>,
});

const emits = defineEmits<{
  (event: 'timerEnded'): void;
}>();

const timer = ref<NodeJS.Timeout>();
const countdown = ref<string>('');
const isTimerActive = ref<boolean>(true);
const expiration = moment(props.endDate);

onMounted(() => {
  updateCountdown()
  timer.value = setInterval(() => {
    updateCountdown();
  }, 1000);
});

const updateCountdown = () => {
  const now = moment();
  const duration = moment.duration(expiration.diff(now));

  if (duration.asSeconds() <= 0) {
    countdown.value = '';
    isTimerActive.value = false;
    emits('timerEnded');
    clearInterval(timer.value);
  } else if (duration.asHours() < DateUtils.HOURS_IN_DAY) {
    const hours = String(duration.hours()).padStart(2, '0');
    const minutes = String(duration.minutes()).padStart(2, '0');
    const seconds = String(duration.seconds()).padStart(2, '0');
    countdown.value = `${hours}:${minutes}:${seconds}`;
  }
}

const timeRemaining = computed(() => {
  const diffHours = expiration.diff(moment(), 'hours');
  if (diffHours < DateUtils.HOURS_IN_DAY) {
    return countdown.value;
  } else {
    const diffDays = moment(props.endDate).diff(moment(), 'days');
    return `${diffDays} ${diffDays > 1 ? I18NGetter().useAttachmentBadge.DAYS : I18NGetter().useAttachmentBadge.DAY}`;
  }
})
</script>
